import { useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { Box, FormHelperText, MenuItem, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import {
  convertDataToMinutes,
  formatReservationLengthToMinutes,
  formatTimeSlotsToMinutes,
  generateReservationSlots,
  generateStartTimeSlots,
  timeConvertToAmPmFormat,
  timeConvertToHrFormat,
} from '../Amenity/utils';

const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 2fr)',
  [theme.breakpoints.between(1536, 1620)]: {
    gridTemplateColumns: 'repeat(5, 2fr)',
  },

  [theme.breakpoints.between('lg', 'xl')]: {
    gridTemplateColumns: 'repeat(4, 2fr)',
  },
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, 2fr)',
  },
  [theme.breakpoints.between(1000, 1200)]: {
    gridTemplateColumns: 'repeat(3, 2fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(6, 2fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(3, 2fr)',
  },
  [theme.breakpoints.down('xs')]: {
    gridTemplateColumns: 'repeat(2, 2fr)',
  },
  marginLeft: '-0.5rem',
}));

const styledProps = [
  'selectedStartTime',
  'selectedDuration',
  'idx',
  'selectedIndex',
  'isCreate',
  'time',
  'findChosenStartTime',
  'findChosenLength',
];

const StyledWrapper = styled(MenuItem, {
  shouldForwardProp: (prop) => !styledProps.includes(prop),
})(
  ({
    theme,
    selectedDuration,
    selectedStartTime,
    idx,
    selectedIndex,
    isCreate,
    time,
    findChosenStartTime,
    findChosenLength,
  }) => ({
    margin: '0.5rem',
    height: 40,
    border: '1px solid #CFCFCF',
    borderRadius: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    ...(!selectedStartTime &&
      !isCreate &&
      time === findChosenStartTime && {
        backgroundColor: theme.palette.primary.main,
        color: '#FFFFFF',
      }),
    ...(!selectedDuration &&
      !isCreate &&
      time === findChosenLength && {
        backgroundColor: theme.palette.primary.main,
        color: '#FFFFFF',
      }),
    ...(selectedIndex === idx && {
      backgroundColor: theme.palette.primary.main,
      color: '#FFFFFF',
    }),
  }),
);

const CustomPickerRow = ({
  isStartTime,
  selectedAmenity,
  reservationDate = [],
  setSelectedStartTime,
  setSelectedDuration,
  selectedStartTime,
  selectedDuration,
  isDayTimePickerDisabled,
  isCreate,
  input,
  meta: { touched, error },
}) => {
  const [selectedIndex, setSelectedIndex] = useState();
  const amenity = useSelector((store) => store.amenity);
  const form = useSelector((store) => store.form.AmenityReservation);

  const hasError = touched && !!error;
  const data = selectedAmenity?.allowedHours || [];
  const minReservationTime = convertDataToMinutes(
    selectedAmenity?.data?.minimumReservationTime,
  );
  const maxReservationTime = convertDataToMinutes(
    selectedAmenity?.data?.maximumReservationTime,
  );

  const findChosenLength = timeConvertToHrFormat(
    convertDataToMinutes(amenity?.amenityReservation?.endTime) -
      convertDataToMinutes(amenity?.amenityReservation?.startTime),
  );

  const findChosenStartTime = timeConvertToAmPmFormat(
    convertDataToMinutes(amenity?.amenityReservation?.startTime),
  );

  const generateDataToRender = () => {
    if (isDayTimePickerDisabled && isStartTime) {
      return [findChosenStartTime];
    }
    if (isDayTimePickerDisabled && !isStartTime) {
      return [findChosenLength];
    }
    if (!isStartTime) {
      return generateReservationSlots(
        data,
        minReservationTime,
        maxReservationTime,
        form.values.startTime,
      );
    }
    if (isStartTime) {
      return generateStartTimeSlots(data, minReservationTime);
    }
    return null;
  };

  const handleOnClick = (index, item) => {
    setSelectedIndex(index);
    input.onChange(item);
    isStartTime
      ? setSelectedStartTime(formatTimeSlotsToMinutes(item))
      : setSelectedDuration(formatReservationLengthToMinutes(item));
  };

  return (
    <>
      <StyledContainer>
        {reservationDate[0] &&
          generateDataToRender().map((time, idx) => (
            <StyledWrapper
              key={time}
              selectedStartTime={selectedStartTime}
              selectedDuration={selectedDuration}
              idx={idx}
              time={time}
              findChosenStartTime={findChosenStartTime}
              findChosenLength={findChosenLength}
              selectedIndex={selectedIndex}
              isCreate={isCreate}
              disabled={isDayTimePickerDisabled}
              onClick={() => handleOnClick(idx, time)}
            >
              <Typography aria-hidden="true">{time}</Typography>
            </StyledWrapper>
          ))}
      </StyledContainer>
      {hasError && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
};

CustomPickerRow.propTypes = {
  isStartTime: PropTypes.bool,
  isDayTimePickerDisabled: PropTypes.bool,
  reservationDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Array),
  ]),
  selectedAmenity: PropTypes.instanceOf(Object),
  setSelectedStartTime: PropTypes.func,
  setSelectedDuration: PropTypes.func,
  selectedStartTime: PropTypes.number,
  selectedDuration: PropTypes.number,
  isCreate: PropTypes.bool,
  meta: PropTypes.instanceOf(Object),
  input: PropTypes.instanceOf(Object),
};

export default CustomPickerRow;
